import { memo } from 'react';

import {
  ImageWrapStyled,
  PartsInnerStyled,
} from '@/components/sections/service/parts/parts-item';
import { Button } from '@/components/shared/buttons/button';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Image } from '@/components/shared/media/image';
import { generateMobileSources } from '@/components/shared/media/image/generate-sources';
import { styled } from '@/stitches.config';
import type { IParts } from '@/types/shared/parts';

const Badge = styled('div', {
  bc: '$primary',
  px: '$space-2',
  py: '$space-1',
  display: 'table-cell',
  br: '$rounded-1',
  width: '$size-10',
  height: '$size-8',
  textAlign: 'center',
});

const CheckList = styled('ul', {
  margin: '$space-0',
  padding: '$space-0',
  columns: 'auto auto',
  '@lg': {
    columns: 'auto 2',
  },
});

interface KeySystemLayoutProps {
  parts: IParts;
  dataTest?: string;
}

export const KeySystemLayout: React.FC<KeySystemLayoutProps> = memo(
  ({ parts, dataTest = 'popup_partsDetail' }: KeySystemLayoutProps) => {
    return (
      <PartsInnerStyled>
        <Grid>
          {!!parts?.image?.src && (
            <ImageWrapStyled>
              <Image
                src={parts?.image.src}
                alt={parts?.image.alt}
                width={600}
                height={257}
                layout="responsive"
                aspectRatio="21by9"
                sources={[
                  {
                    srcset: parts?.image.src,
                    options: {
                      width: 480,
                      crop: '21:9',
                    },
                  },
                  ...generateMobileSources({
                    mobileSrc: parts?.image.src,
                    mobileOptions: {
                      crop: '21:9',
                    },
                  }),
                ]}
              />
            </ImageWrapStyled>
          )}
          <Grid>
            <Box paddingTop="4" paddingBottom="2">
              <Flex direction="row" alignItems="center" columnGap="4">
                <Badge>
                  {!!parts?.remark && (
                    <Text
                      as="h4"
                      variant="text-white"
                      font="bold"
                      dangerouslySetInnerHTML={{
                        __html: parts?.remark,
                      }}
                      data-test={dataTest + '_title'}
                    />
                  )}
                </Badge>
                {!!parts?.title && (
                  <Text
                    as="h3"
                    size="h5"
                    variant="text-black"
                    font="bold"
                    wrap
                    dangerouslySetInnerHTML={{
                      __html: parts?.title,
                    }}
                    data-test={dataTest + '_title'}
                  />
                )}
              </Flex>
            </Box>
            {!!parts?.table?.body?.items && (
              <Box>
                <CheckList>
                  {parts?.table.body.items.map((o, i) => (
                    <li key={i}>
                      {o.item?.map((body, i) => (
                        <Text
                          key={i}
                          as="p"
                          size="text-base-2"
                          variant="text-black"
                          dangerouslySetInnerHTML={{
                            __html: body.title ?? '',
                          }}
                        />
                      ))}
                    </li>
                  ))}
                </CheckList>
              </Box>
            )}
          </Grid>
          {!!parts?.button && (
            <Box marginTop="4" marginX="auto">
              <Button
                label={parts?.button.label}
                size="md"
                border
                href={parts?.button.url}
                target={parts?.button.target}
                test={{
                  dataTest: dataTest + '_button_cta',
                }}
              />
            </Box>
          )}
        </Grid>
      </PartsInnerStyled>
    );
  }
);

KeySystemLayout.displayName = 'SectionPartsItemKeySystemLayout';
